
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const UpdateCredentials = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const type = searchParams.get('type');
  const errorType = searchParams.get('errorType');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    backupCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isBackupOnly, setIsBackupOnly] = useState(false);

  // Determine if we only need backup code based on error type
  useEffect(() => {
    setIsBackupOnly(errorType === 'wrongBA');
  }, [errorType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const cleanOrderId = orderId.startsWith('#') ? orderId.slice(1) : orderId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const payload = isBackupOnly
        ? {
          backupCode: formData.backupCode,
          type: type,
          errorType: 'wrongBA'
        }
        : {
          email: formData.email,
          password: formData.password,
          type: type,
          errorType: 'incorrectCredentials'
        };

      const response = await api.put(`/orders/${cleanOrderId}/credentials`, payload);

      if (response.data.success) {
        navigate('/track-order', {
          state: {
            credentialsUpdated: true,
            message: 'Credentials updated successfully! Your order has been reset to pending status.'
          }
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#191919] text-white p-6">
      <div className="max-w-md mx-auto bg-[#252525] rounded-lg p-6 shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-primary">
          {isBackupOnly ? 'Update Backup Code' : 'Update Credentials'}
        </h1>

        {orderId && type ? (
          <>
            <div className="mb-4">
              <p className="text-gray-400">Order ID: {orderId}</p>
              <p className="text-gray-400">Platform: {type}</p>
              {isBackupOnly && (
                <p className="text-yellow-400 mt-2">Please enter your correct backup code</p>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Rest of the form remains the same */}
              {!isBackupOnly ? (
                <>
                  <div className="mb-4">
                    <label className="block text-gray-300 mb-2">FUT Account Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 bg-[#333] border border-gray-600 rounded focus:outline-none focus:border-primary"
                      required
                      placeholder="Your FUT account email"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-300 mb-2">FUT Account Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full p-3 bg-[#333] border border-gray-600 rounded focus:outline-none focus:border-primary"
                      required
                      placeholder="Your FUT account password"
                    />
                  </div>
                </>
              ) : (
                <div className="mb-6">
                  <label className="block text-gray-300 mb-2">Backup Code</label>
                  <input
                    type="text"
                    name="backupCode"
                    value={formData.backupCode}
                    onChange={handleChange}
                    className="w-full p-3 bg-[#333] border border-gray-600 rounded focus:outline-none focus:border-primary"
                    placeholder="Your 8-digit backup code"
                    maxLength="8"
                    required
                  />
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-900/50 text-red-300 rounded">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-black font-bold py-3 px-4 rounded hover:bg-opacity-90 transition disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </span>
                ) : isBackupOnly ? 'Update Backup Code' : 'Update Credentials'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-red-500 p-4 bg-red-900/50 rounded">
            Missing required parameters in URL. Please use the correct link provided to you.
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateCredentials;